/**
 * ToolCapabilityGate — declarative "this tool requires Web API X" gate.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Tools that need a browser API the device doesn't support (Web Speech on iOS
 * Safari <14.5, AudioWorklet on old Android, MediaRecorder on older Safari …)
 * historically each printed their own bespoke banner — most with copy that was
 * outright wrong on the user's device. The biggest gotcha: "Please use Chrome"
 * advice on iPad, where Chrome iOS uses the same WebKit by App Store policy
 * and gives the same result.
 *
 * This module is the эталон. Each tool declares which Web APIs it needs; the
 * gate inspects [[device-capabilities.js]] (`window.__deviceCap`), and on a
 * mismatch:
 *
 *   1. Hides the tool's main panel (clean removal — memory: «Скрыть полностью»)
 *   2. Renders a localized banner naming the user's detected device + the
 *      missing capability + a *contextual* fix:
 *        - iOS Safari pre-cutoff → "needs iOS 14.5+; this device cannot be
 *          updated; try a newer iPad/iPhone or desktop Chrome/Edge/Safari"
 *        - desktop Firefox / Samsung Internet → "open in Chrome/Edge/Safari"
 *   3. Fires `capability_gate_blocked` telemetry with structured payload so
 *      we can monitor the fleet without per-tool instrumentation.
 *
 * Localized copy lives INLINE (8+1 locales) — same precedent as
 * [[device-capability-gate.js]]. Lets the gate render BEFORE the tool's
 * translation bundle is parsed; defer-order independence.
 *
 * USAGE (per-tool inline IIFE, after `__deviceCap` is loaded):
 *
 *     var verdict = ToolCapabilityGate.require({
 *         slug: 'voice-translator',
 *         apis: ['speech-recognition'],
 *         container: document.querySelector('#app'),      // hidden on block
 *         banner:    document.querySelector('#tcgate'),   // shown on block
 *     });
 *     if (verdict.blocked) return;   // tool initializer bails — gate took over
 *
 * The `banner` element is created by the tool's blade as an empty hidden
 * `<div id="tcgate" class="hidden">` directly above the tool panel. The gate
 * fills it with the banner HTML when blocked.
 *
 * Catalog of known APIs lives here, NOT in tool blades — when iOS or Android
 * raises a cutoff, one file change updates every tool that requires that API.
 */
(function () {
    'use strict';
    if (window.ToolCapabilityGate) return; // duplicate-script-tag safe

    // ── API catalog ───────────────────────────────────────────────────────
    // Each entry: probe (live check), iosMinMajor (the iOS major version that
    // first shipped this API; null = irrelevant to iOS), title key.
    // When adding a new API, only this object and L_API below need an entry.
    var APIS = {
        'speech-recognition': {
            probe: function () { return !!(window.SpeechRecognition || window.webkitSpeechRecognition); },
            iosMinMajor: 14,           // really 14.5; "14+" covers all practical cases since 14.5 was a point release
            iosMinMinor: 5,
            cap: function (c) { return c.hasSpeechRecognition; }
        },
        'audio-worklet': {
            probe: function () { return typeof AudioWorkletNode === 'function'; },
            iosMinMajor: 14,
            iosMinMinor: 5,
            cap: function (c) { return c.hasAudioWorklet; }
        },
        'webgpu': {
            probe: function () { return !!(navigator.gpu && navigator.gpu.requestAdapter); },
            iosMinMajor: 18,           // WebGPU shipped in iOS Safari 18.0
            iosMinMinor: 0,
            cap: function (c) { return c.hasWebGPU; }
        },
        'media-recorder': {
            // MediaRecorder shipped iOS Safari 14.3 (Dec 2020). Same iOS-12-iPad
            // class is blocked as for speech-recognition / audio-worklet.
            probe: function () { return typeof window.MediaRecorder === 'function'; },
            iosMinMajor: 14,
            iosMinMinor: 3,
            cap: function (c) { return typeof window.MediaRecorder === 'function'; }
        }
        // Future: 'web-codecs', 'opfs', 'shared-array-buffer' …
    };

    // ── i18n: short human-readable title for each API ─────────────────────
    var L_API = {
        en: { 'speech-recognition': 'voice recognition', 'audio-worklet': 'real-time audio processing', 'webgpu': 'WebGPU acceleration', 'media-recorder': 'audio/video recording' },
        ru: { 'speech-recognition': 'распознавание речи', 'audio-worklet': 'обработка звука в реальном времени', 'webgpu': 'ускорение WebGPU', 'media-recorder': 'запись аудио и видео' },
        ko: { 'speech-recognition': '음성 인식', 'audio-worklet': '실시간 오디오 처리', 'webgpu': 'WebGPU 가속', 'media-recorder': '오디오·비디오 녹음' },
        ar: { 'speech-recognition': 'التعرف على الصوت', 'audio-worklet': 'معالجة الصوت الفورية', 'webgpu': 'تسريع WebGPU', 'media-recorder': 'تسجيل الصوت والفيديو' },
        id: { 'speech-recognition': 'pengenalan suara', 'audio-worklet': 'pemrosesan audio realtime', 'webgpu': 'akselerasi WebGPU', 'media-recorder': 'perekaman audio/video' },
        pt: { 'speech-recognition': 'reconhecimento de voz', 'audio-worklet': 'processamento de áudio em tempo real', 'webgpu': 'aceleração WebGPU', 'media-recorder': 'gravação de áudio/vídeo' },
        de: { 'speech-recognition': 'Spracherkennung', 'audio-worklet': 'Echtzeit-Audioverarbeitung', 'webgpu': 'WebGPU-Beschleunigung', 'media-recorder': 'Audio-/Videoaufnahme' },
        es: { 'speech-recognition': 'reconocimiento de voz', 'audio-worklet': 'procesamiento de audio en tiempo real', 'webgpu': 'aceleración WebGPU', 'media-recorder': 'grabación de audio/vídeo' },
        fr: { 'speech-recognition': 'reconnaissance vocale', 'audio-worklet': 'traitement audio en temps réel', 'webgpu': 'accélération WebGPU', 'media-recorder': "enregistrement audio/vidéo" },
        uk: { 'speech-recognition': 'розпізнавання мовлення', 'audio-worklet': 'обробка звуку в реальному часі', 'webgpu': 'прискорення WebGPU', 'media-recorder': 'запис аудіо та відео' },
        tr: { 'speech-recognition': 'ses tanıma', 'audio-worklet': 'gerçek zamanlı ses işleme', 'webgpu': 'WebGPU hızlandırma', 'media-recorder': 'ses/video kaydı' }
    };

    // ── i18n: banner shell + variant bodies ───────────────────────────────
    // Variant `ios` (iOS Safari pre-cutoff — honest about iPad lock-in)
    // Variant `browser` (desktop / Android with no SR — direct browser advice)
    // `{api}`     → the human-readable API name from L_API
    // `{detected}` → "iPad on iOS 12.5 Safari" / "Firefox 110 on Windows"
    // `{iosCutoff}` → "iOS 14.5"
    var L_BANNER = {
        en: {
            title: function (api) { return "This tool needs " + api + ", which isn't available on your device."; },
            detected: function (s) { return "Detected: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " requires " + cutoff + " or newer. " +
                       "Older iPads and iPhones can't be updated to that version. " +
                       "To use this tool, try a newer iPad/iPhone, or a desktop computer with Chrome, Edge, Firefox or modern Safari.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " isn't supported in this browser. " +
                       "Open this page in Google Chrome, Microsoft Edge, or Safari to use this tool.";
            }
        },
        ru: {
            title: function (api) { return "Этому инструменту нужно " + api + ", но на вашем устройстве оно недоступно."; },
            detected: function (s) { return "Обнаружено: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " работает с " + cutoff + " или новее. " +
                       "Старые iPad и iPhone обновить до такой версии нельзя. " +
                       "Чтобы воспользоваться инструментом, возьмите более новый iPad/iPhone или ноутбук с Chrome, Edge, Firefox или свежим Safari.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " не поддерживается в этом браузере. " +
                       "Откройте страницу в Google Chrome, Microsoft Edge или Safari, чтобы воспользоваться инструментом.";
            }
        },
        ko: {
            title: function (api) { return "이 도구는 " + api + "가 필요하지만 이 기기에서는 사용할 수 없습니다."; },
            detected: function (s) { return "감지됨: " + s; },
            ios: function (api, cutoff) {
                return api + "에는 " + cutoff + " 이상이 필요합니다. " +
                       "구형 iPad/iPhone은 해당 버전으로 업데이트할 수 없습니다. " +
                       "더 최신 iPad/iPhone을 사용하거나 Chrome, Edge, Firefox 또는 최신 Safari가 설치된 컴퓨터에서 열어 주세요.";
            },
            browser: function (api) {
                return api + "은(는) 이 브라우저에서 지원되지 않습니다. " +
                       "Google Chrome, Microsoft Edge 또는 Safari에서 이 페이지를 열어 주세요.";
            }
        },
        ar: {
            title: function (api) { return "هذه الأداة تحتاج إلى " + api + "، وهي غير متاحة على جهازك."; },
            detected: function (s) { return "تم اكتشاف: " + s; },
            ios: function (api, cutoff) {
                return api + " يتطلب " + cutoff + " أو أحدث. " +
                       "أجهزة iPad وiPhone القديمة لا يمكن تحديثها إلى هذا الإصدار. " +
                       "لاستخدام هذه الأداة، جرّب iPad/iPhone أحدث، أو حاسوباً مكتبياً بمتصفح Chrome أو Edge أو Firefox أو Safari الحديث.";
            },
            browser: function (api) {
                return api + " غير مدعوم في هذا المتصفح. " +
                       "افتح هذه الصفحة في Google Chrome أو Microsoft Edge أو Safari لاستخدام الأداة.";
            }
        },
        id: {
            title: function (api) { return "Alat ini membutuhkan " + api + ", yang tidak tersedia di perangkat Anda."; },
            detected: function (s) { return "Terdeteksi: " + s; },
            ios: function (api, cutoff) {
                return api + " memerlukan " + cutoff + " atau lebih baru. " +
                       "iPad dan iPhone lama tidak bisa diperbarui ke versi tersebut. " +
                       "Untuk menggunakan alat ini, coba iPad/iPhone yang lebih baru, atau komputer desktop dengan Chrome, Edge, Firefox, atau Safari versi terbaru.";
            },
            browser: function (api) {
                return api + " tidak didukung di peramban ini. " +
                       "Buka halaman ini di Google Chrome, Microsoft Edge, atau Safari untuk menggunakan alat ini.";
            }
        },
        pt: {
            title: function (api) { return "Esta ferramenta precisa de " + api + ", que não está disponível neste dispositivo."; },
            detected: function (s) { return "Detectado: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " requer " + cutoff + " ou mais recente. " +
                       "iPads e iPhones antigos não podem ser atualizados para essa versão. " +
                       "Para usar esta ferramenta, tente um iPad/iPhone mais novo ou um computador com Chrome, Edge, Firefox ou Safari atualizado.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " não é suportado neste navegador. " +
                       "Abra esta página no Google Chrome, Microsoft Edge ou Safari para usar a ferramenta.";
            }
        },
        de: {
            title: function (api) { return "Dieses Tool benötigt " + api + ", was auf Ihrem Gerät nicht verfügbar ist."; },
            detected: function (s) { return "Erkannt: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " erfordert " + cutoff + " oder neuer. " +
                       "Ältere iPads und iPhones lassen sich nicht auf diese Version aktualisieren. " +
                       "Verwenden Sie ein neueres iPad/iPhone oder einen Computer mit Chrome, Edge, Firefox oder aktuellem Safari.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " wird in diesem Browser nicht unterstützt. " +
                       "Öffnen Sie diese Seite in Google Chrome, Microsoft Edge oder Safari.";
            }
        },
        es: {
            title: function (api) { return "Esta herramienta necesita " + api + ", que no está disponible en tu dispositivo."; },
            detected: function (s) { return "Detectado: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " requiere " + cutoff + " o más reciente. " +
                       "Los iPads y iPhones antiguos no se pueden actualizar a esa versión. " +
                       "Para usar esta herramienta, prueba un iPad/iPhone más nuevo o un equipo con Chrome, Edge, Firefox o Safari moderno.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " no se admite en este navegador. " +
                       "Abre esta página en Google Chrome, Microsoft Edge o Safari para usar la herramienta.";
            }
        },
        fr: {
            title: function (api) { return "Cet outil nécessite la " + api + ", qui n'est pas disponible sur votre appareil."; },
            detected: function (s) { return "Détecté : " + s; },
            ios: function (api, cutoff) {
                return "La " + api + " requiert " + cutoff + " ou plus récent. " +
                       "Les anciens iPad et iPhone ne peuvent pas être mis à jour vers cette version. " +
                       "Essayez un iPad/iPhone plus récent, ou un ordinateur avec Chrome, Edge, Firefox ou Safari récent.";
            },
            browser: function (api) {
                return "La " + api + " n'est pas prise en charge par ce navigateur. " +
                       "Ouvrez cette page dans Google Chrome, Microsoft Edge ou Safari pour utiliser l'outil.";
            }
        },
        uk: {
            title: function (api) { return "Цьому інструменту потрібно " + api + ", але на вашому пристрої це недоступно."; },
            detected: function (s) { return "Виявлено: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " працює з " + cutoff + " або новішою версією. " +
                       "Старі iPad та iPhone оновити до неї не можна. " +
                       "Щоб скористатися інструментом, візьміть новіший iPad/iPhone або комп’ютер із Chrome, Edge, Firefox чи свіжим Safari.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " не підтримується в цьому браузері. " +
                       "Відкрийте сторінку в Google Chrome, Microsoft Edge або Safari, щоб скористатися інструментом.";
            }
        },
        tr: {
            title: function (api) { return "Bu araç " + api + " gerektiriyor, ancak cihazınızda bu özellik yok."; },
            detected: function (s) { return "Algılanan: " + s; },
            ios: function (api, cutoff) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " için " + cutoff + " veya üzeri gerekir. " +
                       "Eski iPad ve iPhone modelleri bu sürüme güncellenemez. " +
                       "Bu aracı kullanmak için daha yeni bir iPad/iPhone veya Chrome, Edge, Firefox ya da güncel Safari yüklü bir bilgisayar deneyin.";
            },
            browser: function (api) {
                return api.charAt(0).toUpperCase() + api.slice(1) + " bu tarayıcıda desteklenmiyor. " +
                       "Bu aracı kullanmak için sayfayı Google Chrome, Microsoft Edge veya Safari'de açın.";
            }
        }
    };

    function L() {
        var loc = (window.__LOCALE || 'en');
        return {
            banner: L_BANNER[loc] || L_BANNER.en,
            apiName: function (key) {
                var b = L_API[loc] || L_API.en;
                return b[key] || key;
            }
        };
    }

    // ── Device class string for the "Detected:" line. UA-sniff because there
    //    is no equivalent platform API. We're not styling on this — just
    //    confirming the diagnosis so the user trusts the advice. ────────────
    function describeDevice(cap) {
        var ua = cap.ua || '';
        var br = 'unknown browser';
        // Fork tokens first: YaBrowser / OPR / Edg / SamsungBrowser all carry
        // "Chrome/" as well, so a Chrome-first chain misnames every fork.
        if (/YaBrowser\//.test(ua)) br = 'Yandex Browser';
        else if (/Vivaldi\//.test(ua)) br = 'Vivaldi';
        else if (/OPR\//.test(ua)) br = 'Opera';
        else if (/Edg\//.test(ua)) br = 'Edge';
        else if (/SamsungBrowser\//.test(ua)) br = 'Samsung Internet';
        else if (/Firefox\//.test(ua)) br = 'Firefox';
        else if (/Chrome\//.test(ua)) br = 'Chrome';
        else if (/Safari\//.test(ua) && /Version\//.test(ua)) br = 'Safari';

        var os = 'unknown OS';
        if (/iPad/.test(ua))          os = 'iPad';
        else if (/iPhone/.test(ua))   os = 'iPhone';
        else if (/iPod/.test(ua))     os = 'iPod';
        else if (/Android/.test(ua))  os = 'Android';
        else if (/Win(dows)? NT/.test(ua)) os = 'Windows';
        else if (/Mac OS X/.test(ua)) os = 'macOS';
        else if (/Linux/.test(ua))    os = 'Linux';

        if (cap.iOSMajor !== null) {
            return os + ' on iOS ' + cap.iOSMajor + '.' + (cap.iOSMinor || 0) + ' ' + br;
        }
        return os + ' ' + br;
    }

    // ── UA classifier for telemetry (matches session_start emitter shape) ─
    function uaClass(cap) {
        var ua = cap.ua || '';
        var os = 'unknown';
        if (/iPad|iPhone|iPod/.test(ua)) os = 'ios';
        else if (/Android/.test(ua)) os = 'android';
        else if (/Win(dows)? NT/.test(ua)) os = 'windows';
        else if (/Mac OS X/.test(ua)) os = 'macos';
        else if (/Linux/.test(ua)) os = 'linux';

        var br = 'unknown';
        if (/Edg\//.test(ua)) br = 'edge';
        else if (/Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)) br = 'chrome';
        else if (/Firefox\//.test(ua)) br = 'firefox';
        else if (/Safari\//.test(ua) && /Version\//.test(ua)) br = 'safari';

        var osv = '';
        if (cap.iOSMajor !== null) osv = cap.iOSMajor + '.' + (cap.iOSMinor || 0);
        return { ua_os: os, ua_br: br, ua_osv: osv };
    }

    // ── Public: check what API requirements aren't met ────────────────────
    function check(apis) {
        var blockers = [];
        for (var i = 0; i < apis.length; i++) {
            var spec = APIS[apis[i]];
            if (!spec) continue;
            if (!spec.probe()) blockers.push({
                api: apis[i],
                iosCutoff: spec.iosMinMajor + '.' + spec.iosMinMinor
            });
        }
        return blockers;
    }

    // ── Public: install the gate; blocks tool init if any API missing ─────
    function require_(opts) {
        var blockers = check(opts.apis || []);
        if (!blockers.length) {
            window.__tcgateLast = { slug: opts.slug, blocked: false };
            return { blocked: false, blockers: [] };
        }

        var cap = (window.__deviceCap && window.__deviceCap.get())
                  || { isIOS: false, isiOSSafari: false, iOSMajor: null, iOSMinor: null, ua: navigator.userAgent || '' };
        var L_ = L();
        var firstApiHuman = L_.apiName(blockers[0].api);
        var pickVariant = cap.isIOS ? 'ios' : 'browser';
        var detected = describeDevice(cap);
        var cutoff = 'iOS ' + blockers[0].iosCutoff;

        // Render
        if (opts.container) {
            opts.container.style.display = 'none';
            opts.container.setAttribute('aria-hidden', 'true');
        }
        var banner = opts.banner;
        if (banner) {
            banner.innerHTML = ''; // wipe
            var title = document.createElement('div');
            title.className = 'tcgate-title';
            title.textContent = L_.banner.title(firstApiHuman);
            banner.appendChild(title);

            var body = document.createElement('p');
            body.className = 'tcgate-body';
            body.textContent = pickVariant === 'ios'
                ? L_.banner.ios(firstApiHuman, cutoff)
                : L_.banner.browser(firstApiHuman);
            banner.appendChild(body);

            var det = document.createElement('div');
            det.className = 'tcgate-detected';
            det.textContent = L_.banner.detected(detected);
            banner.appendChild(det);

            banner.classList.remove('hidden');
            banner.setAttribute('role', 'alert');
        }

        // Telemetry — structured payload so the fleet dashboard can pivot by
        // OS/browser/missing API without per-tool wiring.
        //
        // Why poll: the layout's `window.__ae` IIFE is inline body AFTER
        // @stack('scripts'). The tool IIFE that calls us runs INSIDE @stack,
        // while the body is still parsing — at that exact moment __ae is
        // undefined, so a direct call lands in the void. setTimeout(0) defers
        // until parsing finishes (incl. the layout's __ae setup at line
        // ~5154 of layouts/app.blade.php), but in slow-server / lazy-defer
        // edge cases that one tick can still race the layout. The poll
        // backstop guarantees delivery as soon as __ae is wired up, capped at
        // ~3 s so the loop self-terminates if __ae never installs (consent
        // declined / DNT). Race + cap confirmed in capability-gate test
        // suite, 2026-06-20.
        var clz = uaClass(cap);
        var beaconPayload = {
            slug: opts.slug,
            missing: blockers.map(function (b) { return b.api; }),
            variant: pickVariant,
            detected: detected,
            ua_os: clz.ua_os,
            ua_br: clz.ua_br,
            ua_osv: clz.ua_osv
        };
        var beaconAttempts = 0;
        function fireBeacon() {
            if (typeof window.__ae === 'function') {
                try { window.__ae('capability_gate_blocked', beaconPayload); } catch (_) {}
                return;
            }
            if (++beaconAttempts < 60) setTimeout(fireBeacon, 50); // ~3 s cap
        }
        setTimeout(fireBeacon, 0);

        window.__tcgateLast = { slug: opts.slug, blocked: true, variant: pickVariant };
        return { blocked: true, blockers: blockers, variant: pickVariant };
    }

    window.ToolCapabilityGate = Object.freeze({
        check: check,
        require: require_,
        // Read-only for testing
        APIS: APIS
    });
})();
